import type { NavGroup, NavLink } from 'src/@core/layouts/types'
import { GYG_NAV_LABELS } from '@/src/gyg-navigation/navLabels'

/**
 * Icon overrides for the BHYVE rail only — classic sidebar still uses `VerticalNavItems` icons.
 * Keys: normalized path (query stripped) where unique; then title; then heuristics for duplicates.
 * Style: MDI outline / rounded for a thin, Figma-friendly left rail.
 */

function stripPath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '')
}

function profilePathKey(p: string): string {
  if (p.startsWith('/app/user-management/profile')) return '/app/user-management/profile'

  return stripPath(p)
}

/** Path-only (unique routes in primary nav). */
const BY_PATH: Record<string, string> = {
  '/app/restaurant': 'mdi:home-outline',
  '/app/restaurant/allocation': 'mdi:account-supervisor-circle-outline',
  '/app/submissions': 'mdi:clipboard-text-clock-outline',
  '/app/disabled-ingredients': 'mdi:food-off-outline',
  '/app/disabled-equipments': 'mdi:toaster-off',
  '/app/disabled-ingredients/reports': 'mdi:file-chart-outline',
  '/app/audit-logs/reports': 'mdi:clipboard-pulse-outline',
  '/app/coach-tenure/reports': 'mdi:account-clock-outline',
  '/app/marketing-portal/store-surcharges': 'mdi:tag-multiple-outline',
  '/app/incomplete-menu-jobs': 'mdi:clipboard-alert-outline',
  '/app/restaurant-insights': 'mdi:chart-line',
  '/app/ai-insights': 'mdi:robot-outline',
  '/app/franchise-hub': 'mdi:office-building-outline',
  '/app/customer-portal': 'mdi:headset',
  '/app/order-management': 'mdi:clipboard-list-outline',
  '/app/products': 'mdi:package-variant-closed',
  '/app/products/mainmenu/mains/5': 'mdi:package-variant-closed',
  '/app/recipe-mgmt': 'mdi:book-open-page-variant-outline',
  '/app/recipe-guide-equipments': 'mdi:blender-outline',
  '/app/food-safety-tags': 'mdi:alert-octagon-outline',
  '/app/portioning-tools': 'mdi:ruler-square-compass',
  '/app/recipe-guide-ingredients': 'mdi:carrot',
  '/app/marketing-portal/offers': 'mdi:tag-heart-outline',
  '/app/marketing-portal/nutritional-info': 'mdi:nutrition',
  '/app/marketing-portal/nutritional-info/used-quantity': 'mdi:counter',
  '/app/user-management': 'mdi:shield-account-outline',
  '/app/user-management/list': 'mdi:format-list-bulleted',
  '/app/user-management/profile': 'mdi:account-circle-outline',
  '/app/user-management/changes': 'mdi:history',
  '/app/faq': 'mdi:help-circle-outline',
  '/app/training-planner/templates': 'mdi:school-outline',
  '/app/training-planner/catalog': 'mdi:cog-outline',
  '/app/training-planner/colour-ref': 'mdi:bookmark-multiple-outline',
  '/app/training-planner/icon-settings': 'mdi:bookmark-multiple-outline',
  '/app/reporting/training-planner': 'mdi:chart-box-outline',
  '/app/announcements': 'tabler:speakerphone',
  '/app/menu-equipments': 'tabler:chef-hat',
  '/app/admin-tools-ingredients': 'tabler:carrot',
  '/app/admin-tools/feature-experiences': 'mdi:flag-variant-outline',
  '/app/prep-config': 'mdi:clipboard-list-outline',
  '/app/cron-jobs': 'mdi:clock-time-four-outline',
  '/app/kitchen-management/oms-restaurants': 'mdi:storefront-outline',
  '/app/kitchen-management/oms-apps': 'mdi:apps',
  '/app/kitchen-management/oms-app-versions': 'mdi:tag-multiple-outline',
  '/app/kitchen-management/oms-products': 'mdi:food-outline',
  '/app/kitchen-management/oms-bundles': 'mdi:food-drumstick-outline',
  '/app/kitchen-management/oms-settings': 'mdi:cog-outline'
}

/** Unambiguous section titles (primary nav). */
const BY_TITLE: Record<string, string> = {
  Home: 'mdi:home-outline',
  Restaurants: 'mdi:storefront-outline',
  'GYG Oracle': 'mdi:chart-box-multiple-outline',
  [GYG_NAV_LABELS.franchiseeHub]: 'mdi:account-group-outline',
  [GYG_NAV_LABELS.guestExperience]: 'mdi:face-agent',
  'Order Management': 'mdi:cart-outline',
  Products: 'mdi:package-variant',
  [GYG_NAV_LABELS.foodGuide]: 'mdi:book-open-variant',
  [GYG_NAV_LABELS.recipes]: 'mdi:book-open-page-variant-outline',
  [GYG_NAV_LABELS.recipeEquipment]: 'mdi:blender-outline',
  [GYG_NAV_LABELS.howToGuides]: 'mdi:comment-question-outline',
  [GYG_NAV_LABELS.quickReference]: 'mdi:comment-question-outline',
  [GYG_NAV_LABELS.restaurantEquipment]: 'tabler:chef-hat',
  [GYG_NAV_LABELS.restaurantPlus]: 'tabler:carrot',
  [GYG_NAV_LABELS.users]: 'mdi:account-multiple-outline',
  'User Management': 'mdi:account-multiple-outline',
  [GYG_NAV_LABELS.reporting]: 'mdi:chart-box-outline',
  [GYG_NAV_LABELS.marketing]: 'mdi:megaphone-outline',
  [GYG_NAV_LABELS.restaurantSurcharges]: 'mdi:tag-multiple-outline',
  Training: 'mdi:notebook-outline',
  [GYG_NAV_LABELS.communicationsAndHelp]: 'tabler:speakerphone',
  [GYG_NAV_LABELS.menuConfig]: 'tabler:chef-hat',
  [GYG_NAV_LABELS.adminTools]: 'mdi:wrench-outline',
  'Kitchen Management': 'mdi:pot-steam-outline',
  [GYG_NAV_LABELS.disabledIngredients]: 'mdi:food-off',
  [GYG_NAV_LABELS.disabledEquipments]: 'mdi:toaster-off',
  [GYG_NAV_LABELS.weeklyDisabledIngredientsReport]: 'mdi:file-chart-outline',
  [GYG_NAV_LABELS.weeklyChangesReport]: 'mdi:clipboard-pulse-outline',
  [GYG_NAV_LABELS.coachTenureReport]: 'mdi:account-clock-outline',
  [GYG_NAV_LABELS.operationalReports]: 'mdi:clipboard-text-clock-outline',
  List: 'mdi:format-list-bulleted',
  'User Role': 'mdi:account-key-outline'
}

export function getBhyveNavRailIcon(item: NavLink | NavGroup): string {
  const title = item.title

  if (item.path) {
    const pk = profilePathKey(item.path)
    const key = stripPath(pk)
    if (BY_PATH[key]) return BY_PATH[key]
  }

  if (BY_TITLE[title]) return BY_TITLE[title]

  if (title === 'Reports' && 'children' in item && item.children?.length) {
    const first = (item.children[0] as NavLink).path
    if (first?.includes('audit-logs')) return 'mdi:clipboard-text-outline'
    if (first?.includes('reporting/training')) return 'mdi:chart-box-outline'
  }

  if (item.icon) return item.icon

  return 'mdi:circle-outline'
}
